import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/home";
import { Link } from "react-router-dom";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const navLinkClasses = "flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap";
  const textClasses = scrolled
    ? "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
    : "text-foreground-900 hover:text-primary-600 hover:bg-foreground-50/30";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? "bg-background-50/95 backdrop-blur-sm border-b border-background-200/70"
            : "bg-background-50/75 backdrop-blur-sm"
        }`}
      >
        <div className="w-full px-4 md:px-8 lg:px-14">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2 flex-shrink-0">
              <span className="font-heading text-xl font-bold text-primary-600 whitespace-nowrap">
                CSSBuy<span className="text-foreground-800">.gay</span>
              </span>
            </a>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-2">
              <Link to="/" className={`${navLinkClasses} ${textClasses}`}>
                <i className="ri-home-line text-sm"></i>
                <span>Home</span>
              </Link>

              <Link to="/artigo/guia-completo-cssbuy-spreadsheet" className={`${navLinkClasses} ${textClasses}`}>
                <i className="ri-book-open-line text-sm"></i>
                <span>Guia de Compras</span>
              </Link>

              {/* Categories Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`${navLinkClasses} ${textClasses}`}
                >
                  <span>Categorias</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200 shadow-lg overflow-hidden max-h-[420px] overflow-y-auto z-50">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.link}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center justify-between px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <span>{cat.name}</span>
                        <i className="ri-arrow-right-up-line text-xs text-foreground-400"></i>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`${navLinkClasses} ${textClasses}`}
              >
                <i className="ri-external-link-line text-sm"></i>
                <span>CSSBuy Sheet</span>
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors cursor-pointer whitespace-nowrap ml-1"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>

            {/* Hamburger Button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg text-foreground-800 hover:bg-background-100 cursor-pointer"
              aria-label="Menu"
            >
              {mobileOpen ? (
                <i className="ri-close-line text-xl"></i>
              ) : (
                <i className="ri-menu-line text-xl"></i>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-16 left-0 right-0 bg-background-50 border-t border-background-200 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="px-4 py-4 space-y-1">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-home-line text-lg"></i>
                <span>Home</span>
              </Link>

              <Link
                to="/artigo/guia-completo-cssbuy-spreadsheet"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-book-open-line text-lg"></i>
                <span>Guia de Compras</span>
              </Link>

              <div className="border-t border-background-200 my-2"></div>

              <p className="px-4 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                Categorias
              </p>

              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.link}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-between px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  onClick={() => setMobileOpen(false)}
                >
                  <span>{cat.name}</span>
                  <i className="ri-arrow-right-up-line text-xs text-foreground-400"></i>
                </a>
              ))}

              <div className="border-t border-background-200 my-2"></div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-external-link-line text-lg"></i>
                <span>CSSBuy Sheet</span>
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 transition-colors cursor-pointer mt-3"
                onClick={() => setMobileOpen(false)}
              >
                Buy It Now
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}